import React, { useState, useEffect, useRef } from 'react';
import { Badge } from './ui';

interface Branch {
  name: string;
  protected: boolean;
  frozen: boolean;
  frozenBy?: string;
  frozenAt?: Date | string;
}

interface BranchSelectorProps {
  owner: string;
  repo: string;
  selectedBranch: string;
  onBranchChange: (branchName: string) => void;
  onBranchesLoaded?: () => void;
}

const STORAGE_KEY = 'yukon_selected_branches';

function getStoredBranch(owner: string, repo: string): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const branches = JSON.parse(stored);
      return branches[`${owner}/${repo}`] || null;
    }
  } catch (e) {
    console.error('Failed to load stored branch:', e);
  }
  return null;
}

function storeSelectedBranch(owner: string, repo: string, branch: string) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const branches = stored ? JSON.parse(stored) : {};
    branches[`${owner}/${repo}`] = branch;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(branches));
  } catch (e) {
    console.error('Failed to store branch:', e);
  }
}

export default function BranchSelector({
  owner,
  repo,
  selectedBranch,
  onBranchChange,
  onBranchesLoaded,
}: BranchSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Initialize selected branch from localStorage on mount
  useEffect(() => {
    const stored = getStoredBranch(owner, repo);
    if (stored && stored !== selectedBranch) {
      // Only use stored branch if it exists
      onBranchChange(stored);
    } else if (!stored && selectedBranch) {
      // No stored branch - store the default branch from props
      storeSelectedBranch(owner, repo, selectedBranch);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadBranches();
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  useEffect(() => {
    // Close dropdown when clicking outside
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const loadBranches = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/repos/${owner}/${repo}/branches`);
      if (!response.ok) {
        throw new Error('Failed to fetch branches');
      }
      const data = await response.json();
      const fetchedBranches = data.branches || [];
      setBranches(fetchedBranches);

      // Validate that the selected branch still exists
      const branchExists = fetchedBranches.some((b: Branch) => b.name === selectedBranch);
      if (!branchExists && fetchedBranches.length > 0) {
        // Selected branch was deleted - reset to default branch
        const defaultBranch =
          fetchedBranches.find((b: Branch) => b.name === 'main' || b.name === 'master')?.name ||
          fetchedBranches[0].name;

        onBranchChange(defaultBranch);
        storeSelectedBranch(owner, repo, defaultBranch);
      }

      // Notify parent that branches have been loaded (so it can refresh repo data)
      if (onBranchesLoaded) {
        onBranchesLoaded();
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBranch = (branchName: string) => {
    onBranchChange(branchName);
    storeSelectedBranch(owner, repo, branchName);
    setIsOpen(false);
  };

  const filteredBranches = branches.filter(branch =>
    branch.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentBranch = branches.find(b => b.name === selectedBranch);

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Branch selector button/pill */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          border: 'none',
          borderRadius: '6px',
          background: isOpen ? 'var(--bg-hover)' : 'var(--bg-color)',
          padding: '6px 10px',
          fontSize: '13px',
          color: 'var(--text-secondary)',
          transition: 'all 0.2s',
        }}
        title="Select branch"
        onMouseEnter={e => {
          if (!isOpen) {
            e.currentTarget.style.color = 'var(--text-primary)';
          }
        }}
        onMouseLeave={e => {
          if (!isOpen) {
            e.currentTarget.style.color = 'var(--text-secondary)';
          }
        }}
      >
        <i className="fa-solid fa-code-branch" style={{ fontSize: '13px' }}></i>
        <span>{selectedBranch}</span>
        <i
          className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`}
          style={{ fontSize: '11px', opacity: 0.6 }}
        ></i>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: `${dropdownRef.current?.getBoundingClientRect().bottom! + 4}px`,
            left: `${dropdownRef.current?.getBoundingClientRect().left}px`,
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            zIndex: 1000,
            minWidth: '280px',
            maxHeight: '400px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Search input */}
          <div style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search branches..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                outline: 'none',
              }}
              onFocus={e => {
                e.target.style.borderColor = 'var(--primary-color)';
              }}
              onBlur={e => {
                e.target.style.borderColor = '#d1d5db';
              }}
            />
          </div>

          {/* Branch list */}
          <div style={{ overflowY: 'auto', maxHeight: '320px' }}>
            {loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                <i className="fa-solid fa-spinner fa-spin"></i>
              </div>
            ) : filteredBranches.length === 0 ? (
              <div
                style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}
              >
                {searchQuery ? 'No branches found' : 'No branches available'}
              </div>
            ) : (
              filteredBranches.map(branch => {
                const isSelected = branch.name === selectedBranch;
                return (
                  <button
                    key={branch.name}
                    onClick={() => handleSelectBranch(branch.name)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      background: isSelected ? '#f3f4f6' : 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      textAlign: 'left',
                      fontSize: '13px',
                      borderBottom: '1px solid #f3f4f6',
                    }}
                    onMouseEnter={e => {
                      if (!isSelected) {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <i
                        className="fa-solid fa-code-branch"
                        style={{
                          color: isSelected ? '#4b5563' : '#6b7280',
                          fontSize: '12px',
                        }}
                      ></i>
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? '#1f2937' : 'inherit',
                        }}
                      >
                        {branch.name}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Badge
                        variant={branch.frozen ? 'frozen' : 'active'}
                        style={{ fontSize: '10px', padding: '2px 6px' }}
                      >
                        {branch.frozen ? 'Frozen' : 'Active'}
                      </Badge>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
